import React, { useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Badge,
    Box,
    IconButton,
    Grid,
    Typography,
    Button,
    Tooltip
} from "@mui/material/";
import { strings } from "../../../../../common/i18n";
import { helpers } from "../../../../../common/lib";
import EditIcon from "@mui/icons-material/Edit";
import ResolveIcon from "@mui/icons-material/BuildCircle";
import { useLocation, useNavigate } from "react-router-dom";
import {
    getIdentifiers,
    renderCircle,
    customBooleanFilterOptions,
    isResolvableAsset,
    vinCheck
} from "./tableHelper";
import { InfoThemeProvider } from "../../../../../common/styles/providers";
import styles from "./styles";
import actions from "../../../../../Store/actions";
import { logToSentry } from "../../../../../config/Sentry";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";

/**
 * useUnexpectedAssets hook (updated)
 *
 * Key changes:
 * - normalize selection to an array of IDs in handleUnexpectedChangeState
 * - compute canResolveAsset & canAcceptSuggestedMatches from IDs (no setState in render)
 * - provide helper to map selected row objects to asset objects when toolbar gives rows
 */

export default function useUnexpectedAssets() {
    const { altAssets, auditReview, typesList, userOrganisations } =
        useSelector((state) => state);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [unexpectedLoading, setUnexpectedLoading] = useState(false);

    // stored selection = array of asset IDs
    const [unexpectedSelectedAssets, setUnexpectedSelectedAssets] = useState(
        []
    );

    const [currentRow, setCurrentRow] = useState(null);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [assetMatchDrawerOpen, setAssetMatchDrawerOpen] = useState(false);
    // showSuggestedMatchButton is computed below from data; keep local toggle for manual hide after accept
    const [showSuggestedMatchButton, setShowSuggestedMatchButton] =
        useState(true);

    const { pathname } = useLocation();

    const handleUnexpectedEdit = useCallback(
        (row) => {
            try {
                navigate(`${pathname}/alt-assets/${row.id}`);
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry(`On unexpected asset row click: ${errStr}`);
                dispatch(
                    actions.toast.create.error(strings("errors.onAssetClick"))
                );
            }
        },
        [navigate, pathname, dispatch]
    );

    const onAcceptSuggestedMatch = useCallback(async () => {
        // build update for assets that are unresolved, not matched and have suggested match
        const result = [];
        for (const asset of Object.values(altAssets.data || {})) {
            const { isResolved, isMatched, assetMatches, suggestedMatch } =
                asset;
            if (
                isResolved === false &&
                !isMatched &&
                Array.isArray(assetMatches) &&
                assetMatches.length > 0 &&
                suggestedMatch
            ) {
                result.push({
                    ...asset,
                    isMatched: true,
                    asset: suggestedMatch?.asset,
                    assetAuxiliary: suggestedMatch?.auxiliary
                });
            }
        }

        if (result.length === 0) {
            // nothing to accept
            return;
        }

        await dispatch(actions.altAssets.create.updateStore(result));
        // hide the button (you had this behaviour previously)
        setShowSuggestedMatchButton(false);
    }, [altAssets, dispatch]);

    /** Helper: map selected row objects (as passed from toolbar) to asset objects */
    const getSelectedUnexpectedAssetsFromRows = useCallback(
        (selectedRows) => {
            if (!selectedRows) return [];
            // selectedRows might be: array of row objects OR array of ids
            if (!Array.isArray(selectedRows)) return [];
            const ids = selectedRows.map((r) => {
                // r can be row object with original or plain id
                if (r && typeof r === "object")
                    return r.original?.id ?? r.id ?? r;
                return r;
            });

            return ids
                .map((id) => helpers.getListItemForId(altAssets, id))
                .filter(Boolean);
        },
        [altAssets]
    );

    /**
     * onResolveDialogOpen: Accept either
     *  - selectedRows (array of row objects) OR
     *  - selectedIds (array of ids)
     *
     * We normalize to ids and store them in state (so other logic uses the selected IDs).
     */
    const onResolveDialogOpen = useCallback(
        (selectedRowsOrIds) => {
            // If caller didn't pass anything, use stored selection
            let ids = [];
            if (!selectedRowsOrIds) {
                ids = [...unexpectedSelectedAssets];
            } else if (Array.isArray(selectedRowsOrIds)) {
                ids = selectedRowsOrIds.map((r) => {
                    if (r && typeof r === "object")
                        return r.original?.id ?? r.id ?? r;
                    return r;
                });
            } else {
                // single id
                ids = [selectedRowsOrIds];
            }
            // normalize: remove falsy and duplicates
            ids = Array.from(new Set(ids.filter(Boolean)));
            setUnexpectedSelectedAssets(ids);
            setResolveDialogOpen(true);
        },
        [unexpectedSelectedAssets]
    );

    const onResolveDialogClose = useCallback(() => {
        setResolveDialogOpen(false);
    }, []);

    const unexpectedColumns = useMemo(
        () => [
            {
                id: "identifier1",
                header: strings("identifier1"),
                accessorKey: "identifier1",
                hideable: false,
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150,
                Cell: ({ row }) => (
                    <Grid container wrap="nowrap" spacing={2}>
                        {row.original.sharedValidationAsset && (
                            <Grid item>
                                <ShareIcon color="info" />
                            </Grid>
                        )}
                        {row.original.identifiersList && (
                            <Grid item xs>
                                {row.original.identifiersList.map(
                                    (item, index) =>
                                        item ? (
                                            <Typography
                                                display="block"
                                                variant="caption"
                                                key={`${row.index}-${index}`}
                                            >
                                                {item}
                                            </Typography>
                                        ) : null
                                )}
                            </Grid>
                        )}
                    </Grid>
                ),
                // Ensure proper copying by providing the actual data value
                accessorFn: (originalRow) => {
                    // Return the actual identifier value for copying
                    return (
                        originalRow.identifier1 ||
                        originalRow.identifiersList?.join(", ") ||
                        ""
                    );
                }
            },
            {
                id: "make",
                header: strings("make"),
                accessorKey: "make",
                filterVariant: "autocomplete",
                size: 120,
                Cell: ({ cell }) => cell.getValue() || "-"
            },
            {
                id: "condition",
                header: strings("condition"),
                accessorKey: "condition",
                filterVariant: "autocomplete",
                size: 100,
                Cell: ({ cell }) => cell.getValue() ?? "–"
            },
            {
                id: "category",
                header: strings("category"),
                accessorKey: "category",
                filterVariant: "autocomplete",
                size: 100,
                Cell: ({ cell }) => cell.getValue() ?? "–"
            },
            {
                id: "possibleAssetMatches",
                header: strings("match"),
                accessorKey: "assetMatches", // This fixes the column ordering
                size: 150,
                Cell: ({ row }) => {
                    const alt = row.original;
                    const hasMatches =
                        Array.isArray(alt.assetMatches) &&
                        alt.assetMatches.length > 0;
                    const isMatched = alt.isMatched === true;
                    const isResolved = alt.isResolved === true;

                    if (!alt.isValidVin) return <></>;

                    if (isResolved) {
                        return (
                            <Box sx={styles.pillResolved}>
                                <CheckCircleIcon
                                    fontSize="small"
                                    color="success"
                                    sx={{ mr: 1 }}
                                />
                                <Typography variant="body2" color="success">
                                    {strings("resolved")}
                                </Typography>
                            </Box>
                        );
                    }

                    if (hasMatches && isMatched) {
                        return (
                            <Box sx={styles.pillMatched}>
                                <Typography
                                    variant="body2"
                                    color="success.main"
                                >
                                    {strings("matched")}
                                </Typography>
                            </Box>
                        );
                    }

                    if (hasMatches && !isMatched) {
                        return (
                            <Box
                                sx={styles.pillMatchButton}
                                onClick={(evt) => {
                                    evt.stopPropagation();
                                    setCurrentRow([alt]);
                                    setAssetMatchDrawerOpen(true);
                                }}
                            >
                                <Typography variant="body2" color="primary">
                                    {`${alt.assetMatches.length} ${strings(
                                        "match"
                                    )}${
                                        alt.assetMatches.length > 1 ? "es" : ""
                                    }`}
                                </Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box sx={styles.pillResolve}>
                            <Typography variant="body2" color="success.info">
                                {strings("resolve")}
                            </Typography>
                        </Box>
                    );
                }
            },
            {
                id: "type",
                header: strings("type"),
                accessorKey: "type",
                filterVariant: "autocomplete",
                size: 120,
                Cell: ({ cell }) => cell.getValue() ?? "–"
            }
        ],
        []
    );

    /** Data mapping for MRT rows */
    const unexpectedData = useMemo(() => {
        const next = (auditReview.altAssets?.ids || []).map((id) => {
            const altAsset = helpers.getListItemForId(altAssets, id) || {};
            const isValidVin = vinCheck(altAsset, typesList);

            const { make, type } = altAsset || {};
            const { makes, assetTypes } = typesList || {};

            const assetMake =
                typeof make === "string"
                    ? make
                    : helpers.getListItemForId(makes, make)?.name;
            const assetType = helpers.getListItemForId(assetTypes, type)?.name;

            return {
                ...altAsset,
                ...getIdentifiers(altAsset),
                isRowSelectable: Boolean(isValidVin),
                isAltAsset: true,
                isValidVin: Boolean(isValidVin),
                isMatched: !!altAsset.isMatched,
                assetActionStatus:
                    altAsset.isResolved === null || !altAsset.isResolved
                        ? true
                        : false,
                make: assetMake,
                matched: `${Boolean(altAsset.assetMatches?.length)}`,
                type: assetType,
                condition: null,
                category: null,
                possibleAssetMatches: altAsset.assetMatches?.length ?? 0
            };
        });

        // sort as before: assets requiring action first/last depending on original intent
        next.sort((a, b) => {
            if (a.assetActionStatus === b.assetActionStatus) return 0;
            return a.assetActionStatus ? -1 : 1;
        });

        return next;
    }, [altAssets, auditReview, typesList]);

    /** -------------------------
     * Selection normalization for MRT state changes
     * MRT's onStateChange may send selection in multiple shapes:
     * - array of ids
     * - object { id: true, id2: true }
     * - rowSelection key etc.
     *
     * Normalize to array of ids and store.
     * ------------------------- */
    const handleUnexpectedChangeState = useCallback(
        (state) => {
            try {
                const { selection } = state || {};
                let normalizedSelection = [];

                if (Array.isArray(selection)) {
                    normalizedSelection = selection;
                } else if (selection && typeof selection === "object") {
                    // object of ids -> boolean
                    normalizedSelection = Object.keys(selection).filter(
                        (k) => selection[k]
                    );
                } else if (
                    state?.rowSelection &&
                    typeof state.rowSelection === "object"
                ) {
                    normalizedSelection = Object.keys(
                        state.rowSelection
                    ).filter((k) => state.rowSelection[k]);
                }

                // ensure unique and filtered
                normalizedSelection = Array.from(
                    new Set(normalizedSelection.filter(Boolean))
                );

                setUnexpectedSelectedAssets(normalizedSelection);
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry(`On audit table state change: ${errStr}`);
                dispatch(
                    actions.toast.create.error(
                        strings("errors.onAuditTableStateChange")
                    )
                );
            }
        },
        [dispatch]
    );

    /** Helper: map stored selected IDs -> asset objects */
    const getSelectedUnexpectedAssets = useCallback(() => {
        if (!unexpectedSelectedAssets || unexpectedSelectedAssets.length === 0)
            return [];
        return unexpectedSelectedAssets
            .map((id) => helpers.getListItemForId(altAssets, id))
            .filter(Boolean);
    }, [unexpectedSelectedAssets, altAssets]);

    /** Compute: canAcceptSuggestedMatches based on stored selection OR globally:
     * This version enables button only when selection contains resolvable suggested matches.
     * If you'd rather show button when ANY asset has suggested matches, compute that from altAssets.
     */
    const canAcceptSuggestedMatches = useMemo(() => {
        if (!unexpectedSelectedAssets || unexpectedSelectedAssets.length === 0)
            return false;

        return unexpectedSelectedAssets.every((id) => {
            const asset = helpers.getListItemForId(altAssets, id);
            if (!asset) return false;
            const { isResolved, isMatched, assetMatches } = asset;
            return (
                isResolved === false &&
                !isMatched &&
                Array.isArray(assetMatches) &&
                assetMatches.length > 0
            );
        });
    }, [unexpectedSelectedAssets, altAssets]);

    /** Global compute: are there ANY suggested matches in dataset? (used to show the AcceptSuggestedMatches button if desired) */
    const hasSuggestedMatchesGlobally = useMemo(() => {
        const all = Object.values(altAssets.data || {});
        return all.some(
            (a) =>
                !a.isResolved &&
                !a.isMatched &&
                Array.isArray(a.assetMatches) &&
                a.assetMatches.length > 0
        );
    }, [altAssets]);

    /** Toolbar builder -- does NOT mutate state. If toolbar receives selectedRows (row objects), we map them locally.
     * Use getSelectedUnexpectedAssetsFromRows when toolbar gets row objects.
     */
    const unexpectedToolBar = useCallback(
        (selectedRows) => {
            // derive selected assets from rows argument if provided (toolbar often passes selected rows)
            const localSelectedAssets =
                getSelectedUnexpectedAssetsFromRows(selectedRows);

            const localSelectedIds = localSelectedAssets.map((a) => a.id);

            // If toolbar didn't pass rows, fallback to stored selection
            const selectedForResolve = localSelectedAssets.length
                ? localSelectedAssets
                : getSelectedUnexpectedAssets();

            const canResolveAsset =
                localSelectedAssets.length > 0 &&
                localSelectedAssets.every((a) => {
                    const isValidVin = vinCheck(a, typesList);
                    if (!isValidVin) return false;

                    // Rule C: must not be resolved yet
                    if (a.isResolved) return false;

                    const hasMatches =
                        Array.isArray(a.assetMatches) &&
                        a.assetMatches.length > 0;

                    // Rule B: If matches exist but not matched, block resolve
                    if (hasMatches && !a.isMatched) return false;

                    // Rule D: No matches => allowed
                    // If matches exist but isMatched = true => allowed

                    return true;
                });

            // determine whether to show AcceptSuggestedMatches: prefer global flag OR selection-based computed flag
            const showAcceptButton =
                showSuggestedMatchButton &&
                (hasSuggestedMatchesGlobally || canAcceptSuggestedMatches);

            return (
                <>
                    {showAcceptButton && (
                        <InfoThemeProvider>
                            <Tooltip title={strings("messages.acceptMatches")}>
                                <Button
                                    startIcon={
                                        <AutoFixNormalIcon sx={{ mr: 1 }} />
                                    }
                                    sx={styles.toolBarButtons}
                                    variant="contained"
                                    onClick={onAcceptSuggestedMatch}
                                >
                                    {strings("acceptSuggestedMatches")}
                                </Button>
                            </Tooltip>
                        </InfoThemeProvider>
                    )}

                    <InfoThemeProvider>
                        <Button
                            sx={styles.toolBarButtons}
                            variant="contained"
                            onClick={() =>
                                onResolveDialogOpen(
                                    localSelectedIds.length
                                        ? localSelectedIds
                                        : undefined
                                )
                            }
                            disabled={!canResolveAsset}
                        >
                            {strings("resolve")}
                        </Button>
                    </InfoThemeProvider>
                </>
            );
        },
        [
            getSelectedUnexpectedAssetsFromRows,
            getSelectedUnexpectedAssets,
            onResolveDialogOpen,
            onAcceptSuggestedMatch,
            canAcceptSuggestedMatches,
            hasSuggestedMatchesGlobally,
            showSuggestedMatchButton,
            styles
        ]
    );

    /** Row actions (same as before) */
    const unexpectedRowActions = useCallback(
        (row) => {
            return [
                <IconButton
                    key="edit"
                    title={strings("edit")}
                    label={strings("edit")}
                    onClick={() => handleUnexpectedEdit(row.original)}
                >
                    <EditIcon color="primary" />
                </IconButton>
            ];
        },
        [handleUnexpectedEdit]
    );

    return {
        unexpectedLoading,
        setUnexpectedLoading,
        unexpectedColumns,
        unexpectedData,
        unexpectedRowActions,
        unexpectedSelectedAssets, // array of IDs

        handleUnexpectedEdit,
        resolveDialogOpen,
        handleUnexpectedChangeState,
        onResolveDialogOpen,
        onResolveDialogClose,
        unexpectedToolBar,
        currentRow,
        assetMatchDrawerOpen,
        setAssetMatchDrawerOpen,
        onAcceptSuggestedMatch,
        canAcceptSuggestedMatches,
        hasSuggestedMatchesGlobally,
        getSelectedUnexpectedAssets // helper returns asset objects from stored IDs
    };
}
